interface TenantTheme {
    appName: string
    logo: { url: string | null }
    favicon: { url: string | null }
    colors: Record<string, string>
    fonts: { heading: string; body: string; headingUrl?: string; bodyUrl?: string }
    borderRadius: string
}

interface TenantPlan {
    name: string
    priceId: string
    price: number
    keepRoyalties: string
    royaltySharePct?: number
    grantsLabelManager?: boolean
    features: string[]
    sale?: {
        enabled: boolean
        percentOff: number
        durationMonths: number
        label?: string | null
        availableUntil?: string | null
    } | null
}

export interface TenantSocials {
    instagram?: string
    tiktok?: string
    youtube?: string
    twitter?: string
    soundcloud?: string
    spotify?: string
    discord?: string
    facebook?: string
}

interface TenantData {
    _id: string | null
    name: string
    slug: string
    theme: TenantTheme
    primaryDomain: string | null
    activeDomain: string | null
    subscriptionsEnabled: boolean
    stripePublishableKey: string | null
    plans: TenantPlan[]
    aiStudioEnabled: boolean
    merchEnabled: boolean
    audioAssetsEnabled: boolean
    workflowsEnabled: boolean
    marketingAiEnabled: boolean
    audioSoftwareEnabled: boolean
    // Shop bundles; the role flags are already ANDed with bundlesEnabled.
    bundlesEnabled?: boolean
    bundlesCreatorsEnabled?: boolean
    bundlesLabelManagersEnabled?: boolean
    tiktokAdsEnabled: boolean
    metaAdsEnabled: boolean
    spotifyAdsEnabled: boolean
    spotifyPlaylistManagerEnabled: boolean
    socialPostingEnabled: boolean
    socialPostingRequireApproval: boolean
    socials?: TenantSocials
    maintenanceMode?: {
        enabled: boolean
        message: string | null
    }
    // Which social-login providers are active for this tenant (drives the
    // login/signup button visibility). Populated by /api/tenant/current.
    socialLogins?: {
        spotify: boolean
        soundcloud: boolean
        facebook: boolean
    }
}

const diztreeSocials: TenantSocials = {
    instagram: 'https://www.instagram.com/diztree/',
    tiktok: 'https://www.tiktok.com/@diztree',
    youtube: 'https://www.youtube.com/@diztree',
    soundcloud: 'https://soundcloud.com/diztree',
    spotify: 'https://open.spotify.com/user/diztree'
}

const psyfeatureSocials: TenantSocials = {
    instagram: 'https://www.instagram.com/psyfeature/',
    soundcloud: 'https://soundcloud.com/psyfeature',
    youtube: 'https://www.youtube.com/@psyfeature'
}

const defaultTenant: TenantData = {
    _id: null,
    name: 'Diztree',
    slug: 'default',
    theme: {
        appName: 'Diztree',
        logo: { url: null },
        favicon: { url: null },
        colors: {},
        fonts: { heading: 'Outfit', body: 'Inter' },
        borderRadius: '0.5rem'
    },
    primaryDomain: null,
    activeDomain: null,
    subscriptionsEnabled: false,
    stripePublishableKey: null,
    plans: [],
    aiStudioEnabled: false,
    merchEnabled: false,
    audioAssetsEnabled: false,
    workflowsEnabled: false,
    marketingAiEnabled: false,
    audioSoftwareEnabled: false,
    bundlesEnabled: false,
    bundlesCreatorsEnabled: false,
    bundlesLabelManagersEnabled: false,
    tiktokAdsEnabled: false,
    metaAdsEnabled: false,
    spotifyAdsEnabled: false,
    spotifyPlaylistManagerEnabled: false,
    socialPostingEnabled: false,
    socialPostingRequireApproval: false,
    maintenanceMode: { enabled: false, message: null },
    // Default to the historically env-backed providers being visible so a
    // missing field never hides a working button; facebook is opt-in.
    socialLogins: { spotify: true, soundcloud: true, facebook: false }
}

export const useTenantTheme = () => {
    const tenant = useState<TenantData>('tenant', () => defaultTenant)

    const appName = computed(() => tenant.value?.theme?.appName || tenant.value?.name || 'Diztree')
    const logoUrl = computed(() => tenant.value?.theme?.logo?.url || '/images/logo.png')
    /**
     * The compact mark, for slots too narrow for the full lockup — a
     * collapsed sidebar rail above all. Falls back to the logo so a
     * tenant that never uploads one still shows something recognisable
     * rather than a gap.
     */
    const iconUrl = computed(() => tenant.value?.theme?.icon?.url || logoUrl.value)
    const faviconUrl = computed(() => tenant.value?.theme?.favicon?.url || '/favicon.ico')

    const socials = computed<TenantSocials>(() => {
        const fromTenant = tenant.value?.socials
        if (fromTenant && Object.values(fromTenant).some(Boolean)) return fromTenant
        const slug = tenant.value?.slug
        if (slug === 'psyfeature') return psyfeatureSocials
        if (!slug || slug === 'default' || slug === 'diztree') return diztreeSocials
        return {}
    })

    const isDiztreeBrand = computed(() => {
        const slug = tenant.value?.slug
        return !slug || slug === 'default' || slug === 'diztree'
    })

    const socialLogins = computed(() =>
        tenant.value?.socialLogins || { spotify: true, soundcloud: true, facebook: false }
    )

    return {
        tenant,
        appName,
        logoUrl,
        iconUrl,
        faviconUrl,
        socials,
        socialLogins,
        isDiztreeBrand
    }
}
